import { createApi } from '@reduxjs/toolkit/query/react';

import { GET, ORGANIZATION_RESOURCE, POST } from '@app/constants';
import { customBaseQuery } from '@app/services/rtkQuery/baseQuery';
import type {
  GetModulesRightsByIdRequest,
  GetOrganizationWithModulesDto,
} from '@app/services/rtkQuery/types';
import type { NAuth, NLogs, WithActiveBranchId } from '@app/types';
import type { NOrganization } from '@app/types/Organization';

const API_URL_PART = ORGANIZATION_RESOURCE;

export const organizationApi = createApi({
  reducerPath: 'organizationApi',
  tagTypes: ['Organizations', 'ModulesRights'],
  baseQuery: (props) => customBaseQuery(props),
  endpoints: ({ mutation, query }) => ({
    RegisterNew: mutation<
      NOrganization.NewOrganizationDTO,
      NOrganization.RegisterNewOrganizationPayload
    >({
      query: (data) => ({
        url: `${API_URL_PART}/RegisterNew`,
        method: POST,
        data,
      }),
    }),
    Search: query<
      NOrganization.SearchOrganizationResponse,
      {
        params: NOrganization.SearchOrganizationPayload;
        headers?: NLogs.Result | unknown;
      }
    >({
      query: ({ params, headers }) => ({
        url: `${API_URL_PART}/Search`,
        method: GET,
        params,
        headers,
      }),
      providesTags: ['Organizations'],
    }),
    GetWithModulesRights: query<
      NOrganization.GetWithModulesRightsDTO,
      NOrganization.GetWithModulesRightsParams
    >({
      query: (params) => ({
        url: `${API_URL_PART}/GetWithModulesRights`,
        method: GET,
        params,
      }),
      providesTags: ['ModulesRights'],
    }),
    GetUsersByOrganization: query<
      NOrganization.GetUsersResponse,
      NOrganization.GetUsersParams
    >({
      query: ({ organizationId, Branches, Positions, Statuses, ...params }) => {
        const formatParams = (key: string, values?: number[] | string[]) =>
          values ? values.map((value) => `${key}=${value}`).join('&') : '';

        const branchParams = formatParams('Branches', Branches);
        const positionParams = formatParams('Positions', Positions);
        const statusParams = formatParams('Statuses', Statuses);

        const queryParams = [branchParams, positionParams, statusParams]
          .filter(Boolean)
          .join('&');

        return {
          url: `${API_URL_PART}/${organizationId}/Users/List${
            queryParams ? `?${queryParams}` : ''
          }`,
          method: GET,
          params,
        };
      },
    }),
    GetAi: query<
      NOrganization.OrganizationUserIdDto[],
      { ActiveBranchId: NAuth.UserPrint['activeBranchId'] }
    >({
      query: (params) => {
        return {
          url: `${API_URL_PART}/GetAi`,
          method: GET,
          params,
        };
      },
    }),
    GetModulesRights: query<GetOrganizationWithModulesDto, WithActiveBranchId>({
      query: (params) => ({
        url: `${API_URL_PART}/GetModulesRights`,
        method: GET,
        params,
      }),
    }),
    GetModulesRightsById: query<
      GetOrganizationWithModulesDto,
      GetModulesRightsByIdRequest
    >({
      query: (params) => ({
        url: `${API_URL_PART}/GetModulesRightsById`,
        method: GET,
        params,
      }),
    }),
    // Organization analytics start
    SearchOrganizationsAnalytics: mutation<
      NOrganization.SearchOrganizationsAnalyticsResponse,
      NOrganization.SearchOrganizationsAnalyticsRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: `${API_URL_PART}/SearchOrganizationsAnalytics`,
        method: POST,
        params: { ActiveBranchId },
        data,
      }),
    }),
    GetOrganizationsAnalyticsPage: query<
      NOrganization.GetOrganizationsAnalyticsBySearchIdResponse,
      NOrganization.GetOrganizationsAnalyticsPageRequest
    >({
      query: (params) => ({
        url: `${API_URL_PART}/GetOrganizationsAnalyticsPage`,
        method: GET,
        params,
      }),
    }),
    // Organization analytics finish
  }),
});

export const {
  useRegisterNewMutation,
  useSearchQuery,
  useGetWithModulesRightsQuery,
  useGetUsersByOrganizationQuery,
  useGetAiQuery,
  useLazyGetAiQuery,
  useGetModulesRightsQuery,
  useLazyGetModulesRightsQuery,
  useGetModulesRightsByIdQuery,
  useLazyGetModulesRightsByIdQuery,
  useSearchOrganizationsAnalyticsMutation,
  useLazyGetOrganizationsAnalyticsPageQuery,
} = organizationApi;
